import { FaWhatsapp, FaLinkedin } from "react-icons/fa";
import { IoLogoWechat } from "react-icons/io5";
import { useState } from "react";

// components
import FormInput from "@/components/FormInput";

// i18n
import { useTranslation } from "react-i18next";
import axios from "axios";

// images
import wechatQrcode from "@/assets/images/contact-us/planetrus-qrcode.jpg";

const ContactUsForm = () => {
  const { t } = useTranslation();
  const [showWechatModal, setShowWechatModal] = useState(false);
  const [notificationModal, setNotificationModal] = useState<{
    show: boolean;
    type: "success" | "error";
    message: string;
  }>({
    show: false,
    type: "success",
    message: "",
  });

  // Add form state
  const [formData, setFormData] = useState({
    name: "",
    businessName: "",
    email: "",
    phone: "",
    message: "",
  });

  const [errors, setErrors] = useState({
    name: "",
    email: "",
    message: "",
  });

  const inputs = [
    {
      name: "name",
      type: "text",
      placeholder: t("contact_us.name_required"),
      error: errors.name,
    },
    {
      name: "businessName",
      type: "text",
      placeholder: t("contact_us.business_name_optional"),
    },
    {
      name: "email",
      type: "email",
      placeholder: t("contact_us.email_required"),
      error: errors.email,
    },
    {
      name: "phone",
      type: "tel",
      placeholder: t("contact_us.phone_optional"),
    },
    {
      name: "message",
      type: "text",
      placeholder: t("contact_us.message_required"),
      error: errors.message,
      isTextArea: true,
    },
  ];

  const validateForm = () => {
    let isValid = true;
    const newErrors = {
      name: "",
      email: "",
      message: "",
    };

    // Name validation
    if (!formData.name.trim()) {
      newErrors.name = t("contact_us.name_required_error");
      isValid = false;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email.trim()) {
      newErrors.email = t("contact_us.email_required_error");
      isValid = false;
    } else if (!emailRegex.test(formData.email)) {
      newErrors.email = t("contact_us.email_invalid_error");
      isValid = false;
    }

    // Message validation
    if (!formData.message.trim()) {
      newErrors.message = t("contact_us.message_required_error");
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const submitForm = () => {
    if (validateForm()) {
      const data = {
        service_id: "service_1wshfsg",
        template_id: "template_y5op8bk",
        user_id: "UhHac88U5z9I7Wbzw",
        template_params: formData,
      };

      axios
        .post("https://api.emailjs.com/api/v1.0/email/send", data, {
          headers: {
            "Content-Type": "application/json",
          },
        })
        .then(() => {
          setNotificationModal({
            show: true,
            type: "success",
            message: t("contact_us.email_sent"),
          });
        })
        .catch((error) => {
          console.error("Failed to send email:", error);
          setNotificationModal({
            show: true,
            type: "error",
            message: t("contact_us.email_sent_error"),
          });
        });
    }
  };

  const handleClickContact = (type: string) => {
    if (type === "wechat") {
      setShowWechatModal(true);
      return;
    }

    const links = {
      whatsapp: "https://wa.me/85292774669",
      linkedin: "https://www.linkedin.com/company/planetrus",
    };
    window.open(links[type as keyof typeof links], "_blank");
  };

  return (
    <>
      <div className="rtl-row-reverse flex flex-col gap-y-6 md:flex-row md:gap-x-9">
        <div className="flex w-full flex-col gap-y-6 md:flex-1">
          {inputs.map((input) => (
            <FormInput
              key={input.name}
              name={input.name}
              type={input.type}
              value={formData[input.name as keyof typeof formData]}
              onChange={handleInputChange}
              placeholder={input.placeholder}
              error={input.error}
              isTextArea={input.isTextArea}
            />
          ))}
          <button
            className="w-full rounded-md bg-primary px-6 py-3 text-base font-bold text-white md:w-auto"
            onClick={submitForm}
          >
            {t("contact_us.submit")}
          </button>
        </div>
        <div className="mt-6 flex w-full flex-col items-center justify-center rounded bg-[url('@/assets/images/contact-us/bg.png')] bg-cover md:mt-0 md:flex-1">
          <div className="w-full p-6 md:p-[76px]">
            <div className="text-center text-3xl font-bold text-white md:text-5xl">{t("contact_us.contact_us")}</div>
            <div className="mt-6 flex flex-col gap-y-6">
              <div className="flex items-center justify-center gap-x-6 md:gap-x-9">
                <div className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-full bg-white">
                  <FaWhatsapp className="h-6 w-6 text-primary" onClick={() => handleClickContact("whatsapp")} />
                </div>
                <div className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-full bg-white">
                  <IoLogoWechat className="h-6 w-6 text-primary" onClick={() => handleClickContact("wechat")} />
                </div>
                <div className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-full bg-white">
                  <FaLinkedin className="h-6 w-6 text-primary" onClick={() => handleClickContact("linkedin")} />
                </div>
              </div>
              <div className="flex flex-col items-center gap-y-2 text-sm font-medium text-white md:flex-row md:flex-wrap md:justify-center md:gap-x-6 md:text-base">
                <div className="flex flex-col items-center gap-y-2 md:flex-row md:gap-x-6">
                  <span>+852 9277 4669</span>
                  <span>+86 198 9659 7151</span>
                </div>
                <span>info@planetrus.com.hk</span>
              </div>
              <div className="text-center text-sm font-medium text-white md:text-base">
                Flat 7, Block B, 5/f, Merit Industrial Centre 94 To Kwa Wan Road To Kwa Wan, Kowloon, Hong Kong
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* WeChat QR Code Modal */}
      {showWechatModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="relative rounded-lg bg-white p-4 md:p-4">
            <button
              className="absolute top-4 text-gray-500 hover:text-gray-700"
              style={{ insetInlineEnd: "1rem" }}
              onClick={() => setShowWechatModal(false)}
            >
              ✕
            </button>
            <div className="flex flex-col items-center gap-6">
              {/* <h3 className="text-xl font-bold">{t("contact_us.scan_wechat")}</h3> */}
              <img src={wechatQrcode} alt="WeChat QR Code" className="max-w-[300px] md:max-w-[400px]" />
            </div>
          </div>
        </div>
      )}

      {/* Notification Modal */}
      {notificationModal.show && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="relative animate-fade-in rounded-lg bg-white p-6 md:p-8">
            <button
              className="absolute top-4 text-gray-500 hover:text-gray-700"
              style={{ insetInlineEnd: "1rem" }}
              onClick={() => setNotificationModal((prev) => ({ ...prev, show: false }))}
            >
              ✕
            </button>
            <div className="flex flex-col items-center gap-4">
              <div className={`text-4xl ${notificationModal.type === "success" ? "text-green-500" : "text-red-500"}`}>
                {notificationModal.type === "success" ? "✓" : "✕"}
              </div>
              <p className="text-center text-lg font-medium text-gray-800">{notificationModal.message}</p>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ContactUsForm;
